import styled from "@emotion/styled";
import {
  Button,
  CircularProgress,
  Pagination,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as React from "react";
import { useSearchParams } from "react-router-dom";

import AttributeSearchInput from "../../components/AttributeSearchInput";
import { COLOR_CONSTANTS } from "../../helpers/Colors";
import {
  approveBulkInvoice,
  downloadPOData,
  getCities,
  getInvoiceList,
} from "../../services/invoiceService";
import LoaderV2 from "./components/LoaderV2";
import OrderFilterCard from "./components/OrderFilter";
import StatusFilter from "./components/StatusFilter";
import DataTable from "./components/InvoiceTable";
import { filterData } from "./constants/filters";
import { useToast } from "./hooks/useToast";
import { Check, Download } from "@mui/icons-material";
import { downloadFileFromArray } from "../../helpers/Utils";
import DownloadPOModal from "./components/DownloadPOModal";
import { UserContext } from "../../context/UserContext";
import { getUserFactories } from "../../services/user";
import { WithPermission } from "./components/WithPermission";
import { BOUNCER_PERMISSIONS } from "../../constants/bouncerPermissions";
import InvoiceReview from "./InvoiceReview";
import CreditNoteReview from "./CreditNoteReview";

const FlexBox = styled(Box)`
  display: flex;
  align-items: center;
`;

const PAGE_SIZE = 10;

function InvoiceList() {
  const { invoiceFactory, user, setInvoiceFactory } =
    React.useContext(UserContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const page = parseInt(searchParams.get("page"), 10) || 1;
  const status = searchParams.get("status") || "awaiting_l2_approval";
  const search = searchParams.get("query") || "";
  const searchBy = searchParams.get("searchBy") || "po_code";

  const [offset, setOffset] = React.useState(null);
  const [filter, setFilter] = React.useState(
    searchParams.get("filterData")
      ? JSON.parse(searchParams.get("filterData"))
      : {}
  );
  const [showDownload, setShowDownload] = React.useState(false);
  const [selectedInvoice, setSelectedInvoice] = React.useState();
  const [selectedCreditNote, setSelectedCreditNote] = React.useState(null);
  const bulkSelection = React.useState([]);
  const [bulkSelectedItems, setBulkSelected] = bulkSelection;

  React.useEffect(() => {
    setOffset(PAGE_SIZE * (page - 1));
  }, [page]);

  // Fetch factories associated with the user
  const {
    isLoading: isFactoryLoading,
    isError: isFactoryError,
    data: factoryList,
  } = useQuery(
    ["factoryListQuery"],
    () =>
      getUserFactories({ userId: user?.email }),
    {
      enabled: user?.id?.length > 1,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  function getFactorycodeForPOList() {
    const hasItemCode = invoiceFactory && invoiceFactory.item_code;
    const hasAllFactories = factoryList?.all_factories;

    if (hasItemCode) {
      if (invoiceFactory.item_code === "all") {
        if (hasAllFactories) return "";
        return getFactoriesFromFactoryMap(factoryList?.factory_map);
      }
      return invoiceFactory.item_code;
    } else if (hasAllFactories) {
      const _factory = {
        item_code: "all",
        item_name: "All factories",
        type: "FCT",
      };
      setInvoiceFactory(_factory);
      localStorage.setItem("invoiceFactory", JSON.stringify(_factory));
      return "";
    }

    let _factory = {};

    if (Object.values(factoryList?.factory_map).length > 1) {
      _factory = {
        item_code: "all",
        item_name: "All factories",
        type: "FCT",
      };
      setInvoiceFactory(_factory);
    } else {
      const factoryCode = Object.keys(factoryList?.factory_map);
      _factory = {
        item_code: factoryCode,
        item_name: factoryList?.factory_map[factoryCode],
        type: "FCT",
      };
      setInvoiceFactory(_factory);
    }
    localStorage.setItem("invoiceFactory", JSON.stringify(_factory));
    return getFactoriesFromFactoryMap(factoryList?.factory_map);
  }

  const {
    isLoading: isCitiesLoading,
    data: citiesData,
  } = useQuery(["citiesQuery"], getCities, {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const {
    isLoading,
    isError,
    data: invoices,
    error,
  } = useQuery(
    ["invoiceListQuery", offset, filter, search, searchBy, status, invoiceFactory],
    () =>
      getInvoiceList({
        offset,
        limit: 10,
        filter,
        search,
        searchBy,
        status,
        factoryCode: getFactorycodeForPOList(),
      }),
    {
      retry: false,
      enabled: offset != null && !isFactoryLoading && !isFactoryError,
    }
  );

  const { isLoading: isDownloading, mutate: downloadData } = useMutation(
    downloadPOData,
    {
      onSuccess: (data) => {
        showToast({
          type: "success",
          message: "Download started",
        });
        downloadFileFromArray(data, "Accounting Data");
      },

      onError: (error) => {
        showToast({ type: "error", message: "Error downloading data" });
      },
    }
  );

  const { isLoading: isApprovalPending, mutate: bulkApprove } = useMutation(
    () =>
      approveBulkInvoice({
        payload: {
          id: bulkSelectedItems,
          role: "L2",
        },
      }),
    {
      onSuccess: (data) => {
        setBulkSelected([]);
      
        const failedInvoices = Object.entries(data).filter(
          ([, value]) => value.status === "FAILED"
        );
        const successFullInvoices = Object.entries(data).filter(
          ([, value]) => value.status === "SUCCESS"
        );

        if(successFullInvoices?.length) {
          showToast({
            type: failedInvoices?.length ? "warning" : "success",
            message: failedInvoices?.length
              ? `${successFullInvoices?.length} invoice(s) approved successfully. ${failedInvoices?.length} invoice(s) couldn't be approved.`
              : "Invoice(s) approved successfully",
          });
        } else {
          showToast({
            type: "error",
            message: "Error approving invoices"
          });
        }

        // Refetch list api
        queryClient.invalidateQueries({ queryKey: ["invoiceListQuery"] });
      },

      onError: (error) => {
        showToast({
          type: "error",
          message: "Error approving invoices: " + error.message,
        });
      },
    }
  );

  const setCurrentPage = (page) => {
    setSearchParams((prev) => {
      prev.set("page", page);
      return prev;
    });
  };

  const resetPage = () => setCurrentPage(1);

  const resetSearchQuery = () => {
    setSearchParams((prev) => {
      prev.delete("query");
      prev.delete("searchBy");
      return prev;
    });
  };

  const _reset = () => {
    resetSearchQuery();
    resetPage();
  };

  const getFactoriesFromFactoryMap = (factoryMap) => {
    return factoryMap ? Object.keys(factoryMap).join(",") : "";
  };

  const handleFilter = (filterData) => {
    const cleanedFilter = Object.fromEntries(
      Object.entries(filterData).filter(([_, v]) =>
        Array.isArray(v) ? v.length > 0 : v !== "" && v != null
      )
    );
    setFilter(cleanedFilter);
    setSearchParams((prev) => {
      prev.set("filterData", JSON.stringify(cleanedFilter));
      prev.set("page", 1);
      return prev;
    });
  };

  if (isLoading || isFactoryLoading) {
    return <LoaderV2 />;
  }
  if (isError)
    return (
      <Box
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        justifyContent={"center"}
        height={"100vh"}
      >
        <Typography variant="h5" color={"red"}>
          {error.message} - Error loading data
        </Typography>
        Try again later
      </Box>
    );

  return (
    <>
      <Box
        display={"flex"}
        alignItems={"start"}
        justifyContent={"flex-start"}
        padding={2}
      >
        {/* Order Filters */}
        <OrderFilterCard
          data={invoices.status_count}
          label={"INVOICES"}
          defaultSelect={"awaiting_l2_approval"}
          paramName="status"
        />
        <Box ml={3} flex={1} sx={{ overflowX: "scroll", height: "100vh" }}>
          {/* Header Actions Start */}
          <FlexBox justifyContent={"space-between"} mb={2}>
            <FlexBox>
              <span className="ft-16 bold">
                Invoice List ({invoices.total_count || 0})
              </span>
              <StatusFilter
                filterData={filterData("invoice")}
                applyFilter={handleFilter}
                selectedFilters={filter}
                cities={citiesData || []}
                citiesLoading={isCitiesLoading}
              />
            </FlexBox>
            <FlexBox>
              <AttributeSearchInput
                value={search}
                selectedAttribute={searchBy}
                onSearch={(attr, val) => {
                  resetPage();
                  setSearchParams((prev) => {
                    prev.set("query", val);
                    prev.set("searchBy", attr);
                    return prev;
                  });
                }}
                onClear={_reset}
              />
              <Box width={"8px"} />
              <WithPermission permission={BOUNCER_PERMISSIONS.SUMMARY_DOWNLOAD}>
                <Button
                  variant="outlined"
                  endIcon={
                    isDownloading ? (
                      <CircularProgress size="12px" />
                    ) : (
                      <Download />
                    )
                  }
                  sx={{
                    color: COLOR_CONSTANTS["primary/default"],
                    borderColor: COLOR_CONSTANTS["primary/default"],
                    textTransform: "none",
                    padding: "8px 15px",
                  }}
                  disabled={isDownloading}
                  onClick={() => {
                    setShowDownload(true);
                  }}
                >
                  Download data
                </Button>
              </WithPermission>
            </FlexBox>
          </FlexBox>
          {/* Header Actions End */}

          {/* Bulk approval */}
          {bulkSelectedItems.length >= 1 ? (
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-start"}
              gap={2}
              marginBottom={2}
            >
              <span className="ft-16 bold">
                {bulkSelectedItems.length} selected
              </span>

              <Button
                variant="contained"
                startIcon={
                  isApprovalPending ? (
                    <CircularProgress size="12px" />
                  ) : (
                    <Check />
                  )
                }
                sx={{
                  backgroundColor: COLOR_CONSTANTS["primary/default"],
                  textTransform: "none",
                  padding: "8px 15px",
                }}
                disabled={isApprovalPending}
                onClick={() => {
                  console.log(bulkSelection[0]);
                  bulkApprove();
                }}
              >
                Approve invoices
              </Button>
            </Box>
          ) : null}

          {/* Data display */}
          <DataTable
            data={invoices.invoice_list || []}
            selectInvoice={setSelectedInvoice}
            selectCreditNote={setSelectedCreditNote}
            bulkSelection={bulkSelection}
          />

          {/* Pagination */}
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            marginLeft={"auto"}
            marginY={3}
          >
            <Pagination
              count={Math.ceil((invoices.total_count || 0) / PAGE_SIZE)}
              variant="outlined"
              shape="rounded"
              page={parseInt(page, 10)}
              onChange={(_, page) => {
                setCurrentPage(page);
              }}
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  color: COLOR_CONSTANTS["on-primary/variant"],
                  backgroundColor: COLOR_CONSTANTS["primary/variant"],
                  borderColor: COLOR_CONSTANTS["on-primary/variant"],
                },
              }}
            />
          </Box>
        </Box>
      </Box>
      {selectedInvoice && (
        <InvoiceReview
          invoice={selectedInvoice}
          handleClose={() => setSelectedInvoice("")}
        />
      )}
      {selectedCreditNote && (
        <CreditNoteReview
          creditNote={selectedCreditNote.creditNote}
          po_code={selectedCreditNote.po_code}
          po_id={selectedCreditNote.po_id}
          handleClose={() => setSelectedCreditNote(null)}
        />
      )}
      {showDownload && (
        <DownloadPOModal
          open={showDownload}
          onClose={() => setShowDownload(false)}
          onDownload={downloadData}
        />
      )}
    </>
  );
}

export default InvoiceList;
